import { transition, trigger, useAnimation } from '@angular/animations';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { take } from 'rxjs';
import {
  fadeInFromTopAnimation,
  fadeOutToTopAnimation,
} from '../../../../../shared/animations/index';
import { SimXReleaseTag } from '../../../../../shared/models/entities/simx-release-tag.model';
import { ReleaseTagPreferredOrder } from '../../../../release-tag/definitions/release-tag-preferred-order.const';
import { ReleaseTagService } from '../../../../release-tag/services/release-tag.service';

@Component({
  selector: 'release-tag-list',
  standalone: false,
  templateUrl: './release-tag-list.component.html',
  styleUrls: ['./release-tag-list.component.scss'],
  host: {
    class: 'angular-component-wrapper',
  },
  animations: [
    trigger('fadeInFromTopOutToTopAnimation', [
      transition(':enter', [
        useAnimation(fadeInFromTopAnimation, {
          params: {
            duration: '0.2s',
            delay: '0s',
          },
        }),
      ]),
      transition(':leave', [
        useAnimation(fadeOutToTopAnimation, {
          params: {
            duration: '0.2s',
            delay: '0s',
          },
        }),
      ]),
    ]),
  ],
})
export class ReleaseTagListComponent implements OnChanges, OnDestroy, OnInit {
  private readonly _releaseTagService = inject(ReleaseTagService);
  private readonly _renderer = inject(Renderer2);

  releaseTagTaxonomy: Array<SimXReleaseTag> = [];
  releaseTagsAvailableToAdd: Array<SimXReleaseTag> = [];
  hasLoadedReleaseTagTaxonomy = false;

  @Input() releaseTags: Array<SimXReleaseTag> = [];
  @Input() allowRemove = false;
  @Input() allowAdd = false;
  @Input() preferredOrder = true;

  @Output('remove') removeEvent: EventEmitter<string> =
    new EventEmitter<string>();
  @Output('add') addEvent: EventEmitter<string> = new EventEmitter<string>();

  addMenuDisplayed = false;
  windowClickListener: () => void;

  ngOnInit(): void {
    this._releaseTagService
      .getReleaseTags()
      .pipe(take(1))
      .subscribe(releaseTags => {
        this.releaseTagTaxonomy = [...releaseTags];
        this.hasLoadedReleaseTagTaxonomy = true;
        this.updateReleaseTagsAvailableToAdd();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['releaseTags']) {
      if (this.hasLoadedReleaseTagTaxonomy) {
        this.updateReleaseTagsAvailableToAdd();
      }

      if (this.preferredOrder) {
        this.releaseTags = changes['releaseTags'].currentValue.sort(
          (a: SimXReleaseTag, b: SimXReleaseTag) =>
            ReleaseTagPreferredOrder.indexOf(a.tag) -
            ReleaseTagPreferredOrder.indexOf(b.tag),
        );
      }
    }
  }

  ngOnDestroy(): void {
    if (this.windowClickListener) {
      this.windowClickListener();
    }
  }

  updateReleaseTagsAvailableToAdd(): void {
    this.releaseTagsAvailableToAdd = this.releaseTagTaxonomy.filter(
      releaseTagTaxonomyItem =>
        this.releaseTags.findIndex(
          releaseTag => releaseTag.tag === releaseTagTaxonomyItem.tag,
        ) < 0,
    );

    if (this.preferredOrder) {
      this.releaseTagsAvailableToAdd.sort(
        (a, b) =>
          ReleaseTagPreferredOrder.indexOf(a.tag) -
          ReleaseTagPreferredOrder.indexOf(b.tag),
      );
    }
  }

  toggleDisplayAddMenu(): void {
    const newState = !this.addMenuDisplayed;
    this.setAddMenuDisplayed(newState);
  }

  setAddMenuDisplayed(state: boolean): void {
    this.addMenuDisplayed = state;

    if (state) {
      this.windowClickListener = this._renderer.listen(
        'window',
        'click',
        (event: Event) => {
          this.setAddMenuDisplayed(false);
        },
      );
    } else {
      if (this.windowClickListener) {
        this.windowClickListener();
      }
    }
  }

  handleTagRemoveAction(releaseTagId: string): void {
    this.removeEvent.emit(releaseTagId);
  }

  handleAddMenuToggle(event: Event): void {
    event.stopPropagation();

    this.toggleDisplayAddMenu();
  }

  handleAddMenuListItemClick(releaseTagId: string): void {
    this.addEvent.emit(releaseTagId);
    this.setAddMenuDisplayed(false);
  }
}
